/**
 * BookOTG Financial Calculation Engine
 * 
 * For any service priced at X LKR:
 * 1. Transport Fee: 1500 LKR (fixed, paid to barber)
 * 2. Net Revenue: X - 1500
 * 3. Service Charge (4%): Net Revenue * 0.04 (platform hosting)
 * 4. Distributable Amount: Net Revenue - Service Charge
 * 5. Barber Share (80%): Distributable * 0.80
 * 6. Owner Share (20%): Distributable * 0.20
 * 7. Total Barber Earnings: Transport Fee + Barber Share
 */
export const TRANSPORT_FEE = 1500;
export const SERVICE_CHARGE_RATE = 0.04;
export const BARBER_SHARE_RATE = 0.80;
export const OWNER_SHARE_RATE = 0.20;

export function calculateBookingFinancials(totalPrice) {
  const price = Number(totalPrice) || 0;
  
  const transportFee = TRANSPORT_FEE;
  const netRevenue = price - transportFee;
  
  const serviceCharge = Math.round(netRevenue * SERVICE_CHARGE_RATE);
  const distributable = netRevenue - serviceCharge;
  
  const barberShare = Math.round(distributable * BARBER_SHARE_RATE);
  const ownerShare = Math.round(distributable * OWNER_SHARE_RATE);
  
  const totalBarberEarnings = transportFee + barberShare;

  return {
    grossAmount: price,
    transportFee,
    netRevenue,
    serviceCharge,
    distributable,
    barberShare,
    ownerShare,
    totalBarberEarnings,
  };
}

export function calculateSummaryFinancials(bookings) {
  const completed = bookings.filter(b => b.status === 'completed');
  
  const totals = completed.reduce(
    (acc, b) => {
      const f = calculateBookingFinancials(b.total_price);
      return {
        grossRevenue: acc.grossRevenue + f.grossAmount,
        totalTransportFees: acc.totalTransportFees + f.transportFee,
        totalServiceCharges: acc.totalServiceCharges + f.serviceCharge,
        totalDistributable: acc.totalDistributable + f.distributable,
        totalBarberShare: acc.totalBarberShare + f.barberShare,
        totalOwnerCommission: acc.totalOwnerCommission + f.ownerShare,
        totalBarberEarnings: acc.totalBarberEarnings + f.totalBarberEarnings,
      };
    },
    {
      grossRevenue: 0,
      totalTransportFees: 0,
      totalServiceCharges: 0,
      totalDistributable: 0,
      totalBarberShare: 0,
      totalOwnerCommission: 0,
      totalBarberEarnings: 0,
    }
  );

  return { ...totals, completedCount: completed.length };
}

export function calculateBarberBreakdown(bookings) {
  const completed = bookings.filter(b => b.status === 'completed');
  const barberMap = {};

  completed.forEach(b => {
    const barberId = b.barber_id;
    const barberName = b.barber?.full_name || 'Unknown Barber';
    const f = calculateBookingFinancials(b.total_price);

    if (!barberMap[barberId]) {
      barberMap[barberId] = {
        barberId,
        barberName,
        bookingCount: 0,
        grossRevenue: 0,
        transportFees: 0,
        serviceCharges: 0,
        barberShare: 0,
        ownerCommission: 0,
        totalBarberEarnings: 0,
      };
    }

    barberMap[barberId].bookingCount += 1;
    barberMap[barberId].grossRevenue += f.grossAmount;
    barberMap[barberId].transportFees += f.transportFee;
    barberMap[barberId].serviceCharges += f.serviceCharge;
    barberMap[barberId].barberShare += f.barberShare;
    barberMap[barberId].ownerCommission += f.ownerShare;
    barberMap[barberId].totalBarberEarnings += f.totalBarberEarnings;
  });

  return Object.values(barberMap).sort((a, b) => b.totalBarberEarnings - a.totalBarberEarnings);
}